import { useEffect } from 'react';
import { createAnalyticsController } from '../../lib/analyticsGating';
import { hasConsent, subscribeConsent } from '../../lib/consent';
import { runOnFirstInteraction } from '../../utils/firstInteraction';

const GA_ID = 'G-01Q6LW9MEE';
const SCRIPT_ID = 'ga-gtag';
const INLINE_ID = 'ga-gtag-inline';

// gtag (~170KB) fuori dal critical path: parte alla prima interazione (o dopo 5s),
// cosi' non pesa su FCP/LCP dei visitatori che non interagiscono.
function loadGtag(): void {
  if (document.getElementById(SCRIPT_ID)) return;
  // Ri-consenso: il kill-switch resta sul window e va riarmato, altrimenti GA non riparte.
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = false;
  const gtag = document.createElement('script');
  gtag.id = SCRIPT_ID;
  gtag.async = true;
  gtag.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(gtag);
  const inline = document.createElement('script');
  inline.id = INLINE_ID;
  inline.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)};gtag('js',new Date());gtag('config','${GA_ID}');`;
  document.head.appendChild(inline);
}

// Revoca: rimuove gli script e spegne GA anche se l'SDK e' gia' in volo (kill-switch ufficiale).
function removeGtag(): void {
  document.getElementById(SCRIPT_ID)?.remove();
  document.getElementById(INLINE_ID)?.remove();
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = true;
}

export function GoogleAnalytics() {
  // GDPR: GA4 non e' essenziale, quindi parte SOLO con consenso analytics (default: negato),
  // come telemetria/Sentry in main.tsx. Il consenso e' bidirezionale: la revoca ferma GA.
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    const controller = createAnalyticsController({
      // Guardia sul consenso al momento dello sparo: la revoca avvenuta durante l'attesa
      // non deve far partire GA (runOnFirstInteraction non e' annullabile).
      start: () => runOnFirstInteraction(() => { if (hasConsent('analytics')) loadGtag(); }, 5000),
      stop: removeGtag,
    });
    const unsubscribe = subscribeConsent((state) => controller.apply(state.categories.analytics));
    controller.apply(hasConsent('analytics'));
    return () => {
      unsubscribe();
      controller.stop();
    };
  }, []);

  return null;
}
